/* eslint-disable max-lines */
import {confirm} from '@baidu/luffy-comp-dialog';
import queryString from 'query-string';
import {v1 as getUUID} from 'uuid';

import toast, {ToastType} from '@/components/Toast';
import KU_QUERY from '@/constants/common/searchParamKeys';
import {getUrlParams, stringify, parse} from '@/utils/kuQuery';
import history from '@/browserHistory';
import {openInSystemBrowser} from '@/utils/browserUtils';
import sdk from '@/sdk';
import {DOC_TYPE} from '@/constants/document/doc';
import {ENVIRONMENT_LEVEL, querystringConfig} from '@/Constant';
import {isDocShortUrl, isDocUrl} from './urlUtils';
import {
    feLogger,
    jsSdkMetingError,
    bdHiJsDiffTime,
    kuSharePv,
    bdHiJsTimeout,
} from '@/buried';

export enum ActionType {
    KuShareLoginReady = 'kushare_login_ready', // 知识库登录成功
    kuShareStart = 'kushare_start', // 库享开始（主持人使用）
    kuShareEnd = 'kushare_end', // 库享结束 （主持人使用）
    kuShareJoin = 'kushare_join', // 加入 (跟随者加入)
    kuShareLeave = 'kushare_leave', // 离开 (跟随者离开)
    kuShareFollow = 'kushare_follow', // 跟随
    kuShareFollowResult ='kushare_follow_result', // 跟随是否成功向端上汇报
    kuShareUnfollow = 'kushare_unfollow', // 非跟随
    kuShareAuthStatus = 'kushare_auth_status', // 有人申请授权（桌面端小铃铛的红点通知）
    kuShareUrlTrace ='kushare_url_trace', // 跳转连接
    kuShareLoadUrl ='kushare_load_url', // 发生路由跳转给端上通知【仅history.push的方式时】
    kuShareGetMeetingInfo = 'kushare_get_meeting_info' // 获取会议信息，包含主持人姓名、端上的language设置
}

export enum MessageType {
    handshake = 'handshake', // 握手
    handshakeSuccess = 'handshake_success', // 握手成功
    oInit = 'o_init', // 共享者上行初始化
    oSnapshot = 'o_snapshot', // 共享者上行提交数据
    fSnapshot = 'f_snapshot', // 广播下行跟随数据
    sChat = 's_chat', // 单波消息，上行(通过表单申请文档权限时同时上行)或下行(需控制端上显示小红点)
    sChatSuccess = 's_chat_success', // 单波消息下行，权限申请成功
    gatewayBroadcast = 'gateway_broadcast', // 接收小红点审批列表中用户的同意/拒绝行为通知
    error = 'error', // 错误消息
}


// 单播消息类型
export enum sChatMsgType {
    manualAuthRequest = 1, // 手动申请权限，用于共享者控制端上展示小红点
    customMessage = 2, // 自定义消息，比如批量授权成功
}

// 封装上报weirwood的方法
export function uploadWeirwoodError(errorData: {reason: string, error?: any, data?: any}) {
    try {
        let originalStack = '';

        if (errorData.error instanceof Error) {
            errorData.reason = `${errorData.reason}-traceId:${window.kuShareTraceId}-原error：${errorData.error.message}`;
            originalStack = errorData.error.stack || '';
        }

        // 删除 error 属性，避免多余的信息被序列化
        delete errorData.error;
        const errorMessage = JSON.stringify(errorData);
        const error = new Error(errorMessage);

        // 将原始捕获的stack拼接到新error的stack后
        if (originalStack) {
            error.stack += `\nOriginal Stack:\n${originalStack}`;
        }

        console.error(error);
    } catch (e) {
        console.error(e);
        console.error(new Error(errorData.reason || ''));
    }
}

// BdHiJs.service.meeting调用异常统一上报，用于统计错误量级
function logSdkError(errorData: {reason: string, error?: any, data?: any}) {
    try {
        if (errorData.error instanceof Error) {
            errorData.error = errorData.error.message;
        }
        const logData = {
            user_guid: sdk.as.user?.userGuid || '',
            rl_id: sdk.as.user?.rlId || '',
            ...errorData,
        };
        feLogger(jsSdkMetingError(logData));
    } catch (e) {
        console.error(e);
    }
}

// 记录BdHiJs加载耗时
function logBdHiJsDiffTime(diffTime: number) {
    try {
        const logData = {
            user_guid: sdk.as.user?.userGuid || '',
            rl_id: sdk.as.user?.rlId || '',
            url: window.location.href,
            userAgent: navigator.userAgent,
            diffTime,
        };
        feLogger(bdHiJsDiffTime(logData));
    } catch (e) {
        console.error(e);
    }
}

// 记录ku享pv
function logKuSharePv() {
    try {
        const logData = {
            user_guid: sdk.as.user?.userGuid || '',
            rl_id: sdk.as.user?.rlId || '',
            userAgent: navigator.userAgent,
        };
        feLogger(kuSharePv(logData));
    } catch (e) {
        console.error(e);
    }
}

// 记录BdHiJs加载超时
function logBdHiJsTimeout() {
    try {
        const logData = {
            user_guid: sdk.as.user?.userGuid || '',
            rl_id: sdk.as.user?.rlId || '',
            userAgent: navigator.userAgent,
            hasBdHiJs: window.BdHiJs ? 1 : 0, // 判断是否已加载BdHiJs
            hasMeetingService: window.BdHiJs?.service?.meeting ? 1 : 0, // 判断BdHiJs是否存在meeting服务
        };
        feLogger(bdHiJsTimeout(logData));
    } catch (e) {
        console.error(e);
    }
}


// 接收端上的传递数据
export function kuShareListener(callBack: any) {
    try {
        console.log('kuShare日志：开始注册端事件监听 service.meeting.kuShareListener------------');
        window.BdHiJs?.service?.meeting?.kuShareListener({
            listener: callBack,
            onsuccess: (data: any) => {
                console.log('kuShare日志：kuShareListener 注册成功 onsuccess----------', data);
            },
            onfail: (err: any) => {
                console.log('kuShare日志：kuShareListener 注册失败 onfail----------', err);
                uploadWeirwoodError({
                    reason: 'kuShare日志：window.BdHiJs.service.meeting.kuShareListener 注册失败 onfail',
                    error: err,
                });
                logSdkError({
                    reason: 'kuShare日志：window.BdHiJs.service.meeting.kuShareListener 注册失败 onfail',
                    error: err,
                });
            },
        });
    } catch (e) {
        console.log('kuShare日志：window.BdHiJs.service.meeting.kuShareListener-------error-----------', e);
        uploadWeirwoodError({
            reason: 'kuShare日志：window.BdHiJs.service.meeting.kuShareListener error',
            error: e,
        });
        logSdkError({
            reason: 'kuShare日志：window.BdHiJs.service.meeting.kuShareListener error',
            error: e,
        });
    }
}

interface actionData {
    action: string;
    token?: string;
    docId?: string;
    name?: string;
    rlId?: string;
    authed?: number;
    disableFollowAction?: number;
    url?: string;
    docPermission?: number;
    code?: number;
    msg?: string;
}

// 向端上传递数据
export function kuShareAction(data: actionData) {
    // kushare_auth_status
    // kushare_unfollow
    // kushare_url_trace
    // kushare_login_ready
    // kushare_end
    try {
        window.BdHiJs?.service?.meeting?.kuShareAction({
            data,
            onsuccess: (data: any) => {
                console.log(data, 'kuShare日志：kuShareAction 注册成功 onsuccess----------');
            },
            onfail: (err: any) => {
                console.log(err, 'kuShare日志：kuShareAction 注册失败 onfail----------');
                uploadWeirwoodError({
                    reason: 'kuShare日志：window.BdHiJs.service.meeting.kuShareAction 注册失败 onfail---------',
                    error: err,
                    data,
                });
                logSdkError({
                    reason: 'kuShare日志：window.BdHiJs.service.meeting.kuShareAction 注册失败 onfail---------',
                    error: err,
                    data,
                });
            },
        });
    } catch (e) {
        console.log('kuShare日志：window.BdHiJs.service.meeting.kuShareAction-------error-----------');
        uploadWeirwoodError({
            reason: 'kuShare日志：window.BdHiJs.service.meeting.kuShareAction error',
            error: e,
            data,
        });
        logSdkError({
            reason: 'kuShare日志：window.BdHiJs.service.meeting.kuShareAction error',
            error: e,
            data,
        });
    }
}

export const localsString: any = {
    'zh-CN': {
        userinfoFailed: '账号信息获取失败，请刷新重试',
        hostWaiting: '跟随共享者失败，请让共享者重进会议或重启客户端',
        unfollow: '已退出跟随，可自由浏览文档',
        followed: '屏幕跟随中，已定位到共享者的位置',
        docChanged: '共享者已切换到其他文档',
        authConfirmTitle: '授权提示',
        authConfirmReadMsg: '是否要给当前参会人授予该页面可阅读权限？',
        authConfirmWriteMsg: '是否要给当前参会人授予该页面可编辑权限？',
        authSuccess: '授权成功',
        authFailed: '授权失败，请刷新重试',
        yourself: '你',
        yourAnd: '你和',
        comma: '、',
        more: '等',
        person: '人',
        noPermission: '无文档阅读权限',
        setSnapshotError: '应用窗口滚动位置时出错，请刷新重试',
        diagramEditing: '您正在编辑流程图，请先保存并返回',
    },
    'en-US': {
        userinfoFailed: 'Failed to get account information, please refresh and try again.',
        // eslint-disable-next-line max-len
        hostWaiting: 'Failed to follow the sharer. Please contact the sharer to rejoin the meeting or restart the client',
        unfollow: 'You have quit following and can browse the document freely.',
        followed: 'In the following screen, the location of the sharer has been located.',
        docChanged: 'The sharer has switched to another document.',
        authConfirmTitle: 'Authorization prompt',
        authConfirmReadMsg: 'Whether to grant read permission on this page to all participants?',
        authConfirmWriteMsg: 'Whether to grant editable permission on this page to all participants?',
        authSuccess: 'Authorization succeeded.',
        authFailed: 'Authorization failed, please refresh and try again.',
        yourself: 'You ',
        yourAnd: 'You and ',
        comma: ', ',
        more: 'etc. A total of ',
        person: ' persons ',
        noPermission: 'have no permission to read the document.',
        setSnapshotError: 'Error when applying scroll position, please refresh and try again.',
        diagramEditing: 'You are editing the flowchart, please save and return first.',
    },
};

export function kuShareToast({content, duration = 3000}: {content: string, duration: number}) {
    console.log(content, 'kuShare日志：kuShareToast-------------------------');
    // 针对会议场景新增一个toast接口，不通用，规则同会议场景的toast提示规则。
    try {
        window.BdHiJs?.service?.meeting?.showToast({
            data: {
                msg: content, // 需要根据中英文环境，配置不同环境的提示话术
                duration: duration, // Number，单位ms，页面可以指定一个显示时间，各端现有能力支持就支持，不支持就走默认逻辑
            },
            onsuccess: (data: any) => {
                console.log(data, 'kuShare日志：kuShareToast 注册成功 onsuccess----------');
            },
            onfail: (err: any) => {
                console.log(err, 'kuShare日志：kuShareToast 注册失败 onfail----------');
                uploadWeirwoodError({
                    reason: 'kuShare日志：window.BdHiJs.service.meeting.showToast 注册失败 onfail----------',
                    error: err,
                    data: {
                        content,
                        duration,
                    },
                });
                logSdkError({
                    reason: 'kuShare日志：window.BdHiJs.service.meeting.showToast 注册失败 onfail----------',
                    error: err,
                    data: {
                        content,
                        duration,
                    },
                });
            },
        });
    } catch (e) {
        console.log('kuShare日志：window.BdHiJs.service.meeting.showToast==========调用失败====================');
        uploadWeirwoodError({
            reason: 'kuShare日志：window.BdHiJs.service.meeting.showToast==========调用失败====================',
            error: e,
            data: {
                content,
                duration,
            },
        });
        logSdkError({
            reason: 'kuShare日志：window.BdHiJs.service.meeting.showToast==========调用失败====================',
            error: e,
            data: {
                content,
                duration,
            },
        });
        toast({
            content,
            duration,
            type: ToastType.Info,
        });
    }
}

export function getMemberList(listener: any) {
    // 获取会议成员列表
    try {
        window.BdHiJs?.service?.meeting?.getMemberList({
            data: {
                type: 0, // 0：所有百度员工， 1：已入会的百度员工
            },
            onsuccess: (data: any) => {
                console.log(data, 'kuShare日志：kuShareMemberList 注册成功 onsuccess----------');
            },
            onfail: (err: any) => {
                console.log(err, 'kuShare日志：kuShareMemberList 注册失败 onfail----------');
                uploadWeirwoodError({
                    reason: 'kuShare日志：kuShare日志：window.BdHiJs.service.meeting.getMemberList 注册失败 onfail----------',
                    error: err,
                });
                logSdkError({
                    reason: 'kuShare日志：kuShare日志：window.BdHiJs.service.meeting.getMemberList 注册失败 onfail----------',
                    error: err,
                });
            },
            listener,
        });
    } catch (e) {
        console.log('kuShare日志：window.BdHiJs.service.meeting.getMemberList==========调用失败====================');
        uploadWeirwoodError({
            reason: 'kuShare日志：kuShare日志：window.BdHiJs.service.meeting.getMemberList==========调用失败====================',
            error: e,
        });
        logSdkError({
            reason: 'kuShare日志：kuShare日志：window.BdHiJs.service.meeting.getMemberList==========调用失败====================',
            error: e,
        });
        // todo 注意：线上不能放开，仅用于本地调试数据
        // listener({
        //     memberList: [
        //         {uid: '3000010959', name: '武彦村'},
        //         {uid: '3000010964', name: '李子桦'},
        //         {uid: '3000010961', name: '龙曦'},
        //         {uid: '3000011063', name: '普恒恒'},
        //     ],
        // });
    }
}

// 获取批量授权时授予什么权限，如果是新建的文档授予可编辑权限，否则只读权限
export function getRequestAuthType() {
    const query = getUrlParams();
    const newCreate = query?.newCreate || '';
    return newCreate === '1' ? 'w' : 'r';
}

export function kuShareConfirm(title: string, desc: string, listener: any) {
    try {
        window.BdHiJs?.service?.meeting?.showYesno({
            data: {
                cancelable: 2, // 1 为 alert 2 为confirm
                title: title,
                desc: desc,
                yes: {
                    name: '确定',
                    value: 'submitValue', // 确定时 listener中返回的值
                },
                no: {
                    name: '取消',
                    value: 'cancelValue', // 取消时 listener中返回的值
                },
            },
            onsuccess: (data: any) => {
                console.log(data, 'kuShare日志：kuShareConfirm 注册成功 onsuccess----------');
            },
            onfail: (err: any) => {
                console.log(err, 'kuShare日志：kuShareConfirm 注册失败 onfail----------');
                uploadWeirwoodError({
                    reason: 'kuShare日志：window.BdHiJs.appnative.dialog.showYesno 注册失败 onfail----------',
                    error: err,
                    data: {
                        title,
                        desc,
                    },
                });
                logSdkError({
                    reason: 'kuShare日志：window.BdHiJs.appnative.dialog.showYesno 注册失败 onfail----------',
                    error: err,
                    data: {
                        title,
                        desc,
                    },
                });
            },
            listener: listener,
        });
    } catch (e) {
        console.log('kuShare日志：window.BdHiJs.appnative.dialog.showYesno==========调用失败====================');
        uploadWeirwoodError({
            reason: 'kuShare日志：window.BdHiJs.appnative.dialog.showYesno==========调用失败====================',
            error: e,
            data: {
                title,
                desc,
            },
        });
        logSdkError({
            reason: 'kuShare日志：window.BdHiJs.appnative.dialog.showYesno==========调用失败====================',
            error: e,
            data: {
                title,
                desc,
            },
        });
        confirm({
            message: desc,
            onConfirm: () => {
                listener('submitValue');
            },
            onCancel: () => {
                console.log('kuShare日志：kuShareConfirm -- cancel');
            },
        });
    }
}

interface mouseEventData {
    event: string;
    xposition: number;
    yposition: number;
}

export function kuShareMouseEvent(data: mouseEventData) {
    try {
        window.BdHiJs.appnative.webview.mouseEvent({
            data,
            onsuccess: (data: any) => {
                console.log(data, 'kuShare日志：kuShareMouseEvent 注册成功 onsuccess----------');
            },
            onfail: (err: any) => {
                console.log(err, 'kuShare日志：kuShareMouseEvent 注册失败 onfail----------');
                uploadWeirwoodError({
                    reason: 'kuShare日志：kuShareMouseEvent 注册失败 onfail----------',
                    error: err,
                    data,
                });
                logSdkError({
                    reason: 'kuShare日志：kuShareMouseEvent 注册失败 onfail----------',
                    error: err,
                    data,
                });
            },
        });
    } catch (e) {
        console.log('kuShare日志：window.BdHiJs.appnative.webview.mouseEvent==========调用失败====================');
        uploadWeirwoodError({
            reason: 'kuShare日志：kuShare日志：window.BdHiJs.appnative.webview.mouseEvent==========调用失败====================',
            error: e,
            data,
        });
        logSdkError({
            reason: 'kuShare日志：kuShare日志：window.BdHiJs.appnative.webview.mouseEvent==========调用失败====================',
            error: e,
            data,
        });
    }
}

export function getDocUrlParams(url: string): any {
    if (!url) {
        return {};
    }
    if (url.includes('#')) {
        url = url.split('#')[0];
    }
    if (url.includes('?')) {
        url = url.split('?')[0];
    }
    if (isDocShortUrl(url)) {
        const docGuid = url.split('/d/')[1];
        return {docGuid: docGuid.split('/')[0]};
    }
    if (!isDocUrl(url) && !url.startsWith('/')) {
        return {};
    }
    if (url.includes('/knowledge/')) {
        url = url.split('/knowledge/')[1];
    }
    const array = url.split('/');
    return {
        spaceGuid: array[0],
        groupGuid: array[1],
        repositoryGuid: array[2],
        docGuid: array[3],
    };
}

// 是否与当前页面相同知识库
export function isSameRepo(url: string) {
    if (!url) {
        return false;
    }
    const {repositoryGuid} = getDocUrlParams(url);
    if (!repositoryGuid) {
        return false;
    }
    const current = getDocUrlParams(window.location.origin + window.location.pathname);
    return repositoryGuid && current.repositoryGuid && repositoryGuid === current.repositoryGuid;
}

export function isSameDoc(url: string) {
    if (!url) {
        return false;
    }
    const {repositoryGuid, docGuid} = getDocUrlParams(url);
    if (!repositoryGuid || !docGuid) {
        return false;
    }
    const current = getDocUrlParams(window.location.origin + window.location.pathname);
    return (repositoryGuid && current.repositoryGuid && repositoryGuid === current.repositoryGuid)
        && (docGuid && current.docGuid && docGuid === current.docGuid);
}

export function replaceQueryFollowed(history: any, followed: boolean) {
    const query = getUrlParams();
    console.log('kuShare日志：----------------replaceQueryFollowed-------------', followed, query?.followed);
    if ((!followed && query?.followed === '0') || (followed && query?.followed === undefined)) {
        return;
    }
    history.replace({
        ...history.location,
        search: stringify({...query, [KU_QUERY.space.followed]: followed ? undefined : 0}),
    });
}

export function isInKuShare() {
    const query = getUrlParams();
    return (query?.action === ActionType.kuShareStart || query?.action === ActionType.kuShareJoin) && query?.token;
}

export function getKuShareMode() {
    const query = getUrlParams();
    if (query?.action === ActionType.kuShareStart) {
        return ActionType.kuShareStart;
    } else if (query?.action === ActionType.kuShareJoin) {
        return ActionType.kuShareJoin;
    } else {
        return '';
    }
}

// 获取与Ku享相关url的query部分
export function getKuShareQuery(kuShareParams?: any): string {
    const query = getUrlParams();
    if (kuShareParams) {
        // followed 是否正在跟随的状态，在url参数上单独缓存，页面整体刷新跟随状态保持
        return `?${stringify({...kuShareParams, [KU_QUERY.space.followed]: query?.followed})}`;
    } else {
        const kuShareQuery = {
            action: query?.action,
            token: query?.token,
            lang: query?.lang,
            followed: query?.followed,
        };
        return `?${stringify(kuShareQuery)}`;
    }
}

function parseUrl(url: string) {
    const pattern = RegExp('^(([^:/?#]+):)?(//([^/?#]*))?([^?#]*)(\\?([^#]*))?(#(.*))?');
    const matches = pattern.exec(url);
    if (!matches) {
        return {protocol: '', host: '', pathname: '', search: '', hash: ''};
    }
    return {
        protocol: matches[2] ? `${matches[2]}:` : '',
        host: matches[4] || '',
        pathname: matches[5] || '',
        search: matches[7] ? `?${matches[7]}` : '',
        hash: matches[9] ? `#${matches[9]}` : '',
    };
}

// 检查url的query参数中有没有和Ku享冲突的参数，如果有就去掉
function getUrlCleanQuery(search: string) {
    const urlQuery = parse(search);
    const params = ['action', 'token', 'followed', 'lang'];
    params.forEach((name: string) => {
        if (urlQuery[name]) {
            delete urlQuery[name];
        }
    });
    console.log('kuShare日志：----getUrlCleanQuery--------', urlQuery);
    return stringify(urlQuery);
}

// 获取与Ku享相关url的path部分
export function getKuShareRouterPath(url: string, kuShareParams?: any): string {
    if (!url) {
        return '';
    }
    let query = getKuShareQuery(kuShareParams);
    // 如果是以'/'开头的路由，直接返回
    if (url.startsWith('/')) {
        return `${url}${query}`;
    }
    if (!url.includes('://')) {
        url = `http://${url}`;
    }
    let urlObj = null;
    try {
        urlObj = new URL(url);
    } catch (e) {
        urlObj = parseUrl(url);
    }
    if (urlObj.search) {
        const cleanQuery = getUrlCleanQuery(urlObj.search);
        if (cleanQuery) {
            query += `&${cleanQuery}`;
        }
    }
    const path = urlObj.pathname.startsWith('/m/') ? urlObj.pathname.replace('/m/', '/') : urlObj.pathname;
    return `${path}${query}${urlObj.hash}`;
}

export function getKuShareUrl(url: string, kuShareParams?: any): string {
    if (!url) {
        return '';
    }
    let query = getKuShareQuery(kuShareParams);
    if (url.startsWith('/')) {
        return `${window.location.origin}${url}${query}`;
    }
    if (!url.includes('://')) {
        url = `http://${url}`;
    }
    let urlObj = null;
    try {
        urlObj = new URL(url);
    } catch (e) {
        urlObj = parseUrl(url);
    }
    if (urlObj.search) {
        const cleanQuery = getUrlCleanQuery(urlObj.search);
        if (cleanQuery) {
            query += `&${cleanQuery}`;
        }
    }
    const path = urlObj.pathname.startsWith('/m/') ? urlObj.pathname.replace('/m/', '/') : urlObj.pathname;
    const protocol = process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT_LEVEL.online ||
        process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT_LEVEL.gray ? 'https:' : urlObj.protocol;
    return `${protocol}//${urlObj.host}${path}${query}${urlObj.hash}`;
}

// 发生路由跳转给端上通知【仅history.push的方式时】
export function onKuShareRouterPush(url: string) {
    const query = getUrlParams();
    const token = typeof query?.token === 'string' ? query?.token : '';
    kuShareAction({
        action: ActionType.kuShareLoadUrl,
        token: token,
        url: url,
    });
}

// 适配当前页锚点跳转
function locationLoadUrl(url: string) {
    const reg = new RegExp(`${window.location.pathname}.*#.*`); // 与当前文档url相同且带有锚点
    if (reg.test(url)) {
        window.location.href = url;
        setTimeout(() => {
            window.location.reload();
        }, 0);
    } else {
        window.location.href = url;
    }
}

function jumpByCheckDocType(url: string, history: any) {
    const {docGuid} = getDocUrlParams(url);
    if (!docGuid) {
        kuShareToast({content: '链接错误，缺少文档ID', duration: 3000});
        return;
    }
    sdk.kuShare.getDocInfo({docIds: [docGuid]}).then(res => {
        console.log('kuShare日志：获取文档基本信息-------', res);
        const docs = res.docs || {};
        const info = docs[docGuid] || {};
        if (!info.uri && isDocShortUrl(url)) {
            kuShareToast({content: '获取链接信息失败，请重试', duration: 3000});
            return;
        }
        if (info.uri && info.uri !== url && process.env.REACT_APP_ENVIRONMENT !== ENVIRONMENT_LEVEL.local) {
            const link = new URL(url);
            const query = parse(link.search, querystringConfig);
            url = queryString.stringifyUrl({url: info.uri, query}, querystringConfig);
        }
        if (info && info.type === DOC_TYPE.SHEET || info.type === DOC_TYPE.DATASHEET) {
            console.log('kuShare日志：表格或数据包url----------------------', url);
            openInSystemBrowser(url.startsWith('/') ? `${window.location.origin}${url}` : url);
            return;
        }
        // Ku享模式下，仅知识库内的文档链接在端内会议中直接打开，否则系统自带浏览器打开
        if (history && isSameRepo(url) && !isSameDoc(url)) {
            history.push(getKuShareRouterPath(url));
            onKuShareRouterPush(url);
        } else {
            console.log('kuShare日志：clickUrlInKuShare----window.location.href= ---------', getKuShareUrl(url));
            locationLoadUrl(getKuShareUrl(url));
        }
    }).catch(err => {
        console.log('kuShare日志：jumpByCheckDocType调用异常---', err);
        kuShareToast({content: '获取链接信息失败，请重试', duration: 3000});
    });
}

// 在Ku享模式下点击链接【确定是知识库文档】
export function clickDocUrlInKuShare(url: string) {
    const kuShareMode = getKuShareMode();
    // 在任何地方点击url链接，首先退出跟随，替换url上followed参数，再进行跳转
    if (kuShareMode === ActionType.kuShareJoin) {
        replaceQueryFollowed(history, false);
    }
    if (!url) {
        return;
    }
    jumpByCheckDocType(url, history);
}

// 在Ku享模式下点击链接【不一定是知识库文档】
export function clickUrlInKuShare(url: string) {
    const kuShareMode = getKuShareMode();
    // 在任何地方点击url链接，首先退出跟随，替换url上followed参数，再进行跳转
    if (kuShareMode === ActionType.kuShareJoin) {
        replaceQueryFollowed(history, false);
    }
    if (!url) {
        return;
    }
    if (isDocUrl(url) || isDocShortUrl(url)) {
        jumpByCheckDocType(url, history);
    } else {
        openInSystemBrowser(url);
    }
}

/**
 * ku享场景dom渲染函数。确保在onBdHiJsReady后再渲染dom，并增加5000毫秒超时处理
 *
 * @param renderApp 负责渲染React组件到DOM中
 */
export function kuShareRender(renderApp: () => void) {
    let onBdHiJsReadyPromiseResolve: (() => void) | null = null;
    let onBdHiJsReadyTime: number | null = null;
    let onloadTime: number | null = null;

    // 这个Promise用于等待onBdHiJsReady
    const onBdHiJsReadyPromise = new Promise<void>((resolve) => {
        onBdHiJsReadyPromiseResolve = resolve;
    });

    // 监听onBdHiJsReady事件
    window.onBdHiJsReady = () => {
        console.log('kuShare日志：onBdHiJsReady----------------');
        window.kuShareTraceId = getUUID();
        if (onBdHiJsReadyPromiseResolve) {
            onBdHiJsReadyPromiseResolve();
        }

        onBdHiJsReadyTime = Date.now();
        // 如果onBdHiJsReady已经触发，则立即计算时间差
        if (onloadTime !== null) {
            const diffTime = onBdHiJsReadyTime - onloadTime;
            console.log(`onBdHiJsReady - onload 时间差: ${diffTime}毫秒`);
            logBdHiJsDiffTime(diffTime);
        }

        // bdhijs ready后，可能不存在meeting
        if (!window.BdHiJs?.service?.meeting) {
            uploadWeirwoodError({
                reason: 'kuShare日志：onBdHiJsReady触发，没有BdHiJs.service.meeting',
                data: {
                    hasBdHiJs: window.BdHiJs ? 1 : 0,
                    hasMeetingService: window.BdHiJs?.service?.meeting ? 1 : 0,
                },
            });
            logBdHiJsTimeout();
        }
    };

    // 监听onload事件
    window.onload = () => {
        console.log('kuShare日志：onload----------------');

        onloadTime = Date.now();
        // 如果onBdHiJsReady已经触发，则立即计算时间差
        if (onBdHiJsReadyTime !== null) {
            const diffTime = onBdHiJsReadyTime - onloadTime;
            console.log(`onBdHiJsReady - onload 时间差: ${diffTime}毫秒`);
            logBdHiJsDiffTime(diffTime);
        }
    };

    logKuSharePv();
    console.log('kuShare日志：正在等待onBdHiJsReady--------------');
    Promise.race([
        onBdHiJsReadyPromise,
        new Promise<never>((_, reject) => setTimeout(reject, 2000)),
    ])
        .then(() => {
            renderApp();
        })
        .catch(() => {
            // 等待2s超时，直接渲染renderApp，并上报
            if (!window.BdHiJs?.service?.meeting) {
                uploadWeirwoodError({
                    reason: 'kuShare日志：onBdHiJsReady超时，没有BdHiJs.service.meeting',
                    data: {
                        hasBdHiJs: window.BdHiJs ? 1 : 0,
                        hasMeetingService: window.BdHiJs?.service?.meeting ? 1 : 0,
                    },
                });
                logBdHiJsTimeout();
            }
            renderApp();
        });

}
